import { Injectable } from '@angular/core';

const getStringKey = (key: ELocalStorageKeys): string => ELocalStorageKeys[key];

@Injectable({
  providedIn: 'root',
})
export class LocalStorageService {
  private getItemAsString(key: ELocalStorageKeys): string {
    return localStorage.getItem(getStringKey(key));
  }

  private getParsedItem<T>(key: ELocalStorageKeys): T {
    const item = this.getItemAsString(key);
    let itemAsAny;
    try {
      itemAsAny = JSON.parse(item);
    } catch (err) {
      console.warn('cannot get obj from item ', item, ' key', getStringKey(key));
      itemAsAny = null;
    }

    return itemAsAny;
  }

  getSelectedLanguageFromStore() {
    return this.getItemAsString(ELocalStorageKeys.selectedSystemLanguageID);
  }

  putSelectedLanguageToStore(langId: number) {
    this.putItemToLocalStorage(ELocalStorageKeys.selectedSystemLanguageID, langId);
  }

  putQueryParamToLocalStorage(key: string, item: any) {
    const params = this.getParsedItem<any>(ELocalStorageKeys.queryParams) || {};
    params[key] = item;
    this.putItemToLocalStorage(ELocalStorageKeys.queryParams, params);
  }

  getQueryParamFromLocalStorage(key: string) {
    const params = this.getParsedItem<any>(ELocalStorageKeys.queryParams) || {};
    return params[key];
  }

  removeQueryParamFromLocalStorage(key: string) {
    this.putQueryParamToLocalStorage(key, undefined);
  }

  clearQueryParamsFromLocalStorage() {
    removeItemFromLocalStorage(ELocalStorageKeys.queryParams);
  }

  private putItemToLocalStorage(key: ELocalStorageKeys, item: any) {
    const stringKey = getStringKey(key);
    let itemForSave = item;

    if (item instanceof Object) {
      try {
        itemForSave = JSON.stringify(item);
      } catch (err) {
        console.error(err);
        const errMessage = 'cannot convert item to object ' + item + ' key' + getStringKey(key);
        throw new Error(errMessage);
      }
    }

    localStorage.setItem(stringKey, itemForSave);
  }

  saveClientAliasForMobile(clientAlias: string): void {
    this.putItemToLocalStorage(ELocalStorageKeys.loginAlias, clientAlias);
  }

  getClientAliasForMobile(): string {
    return this.getItemAsString(ELocalStorageKeys.loginAlias);
  }

  removeClientAliasForMobile(): void {
    removeItemFromLocalStorage(ELocalStorageKeys.loginAlias);
  }

  putCSRFToken(token: string) {
    this.putItemToLocalStorage(ELocalStorageKeys.xsrfToken, token);
  }

  getCSRFToken() {
    return this.getItemAsString(ELocalStorageKeys.xsrfToken);
  }

  putEmail(email: string) {
    this.putItemToLocalStorage(ELocalStorageKeys.email, email);
  }

  getEmail(): string {
    return this.getItemAsString(ELocalStorageKeys.email) ?? '';
  }

  putReturnUrl(returnUrl: string) {
    this.putItemToLocalStorage(ELocalStorageKeys.returnUrl, decodeURIComponent(returnUrl));
  }
}

export enum EQueryStringParams {
  returnUrl = 'ReturnUrl',
  isMobile = 'isMobile',
  clientAlias = 'clientAlias',
  token = 'token',
  appVer = 'appVer',
}

export enum ELocalStorageKeys {
  selectedSystemLanguageID = 'selectedSystemLanguageID',
  xsrfToken = 'xsrfToken',
  returnUrl = 'returnUrl',
  queryParams = 'queryParams',
  loginAlias = 'loginAlias',
  email = 'email',
}

export function removeItemFromLocalStorage(key: ELocalStorageKeys) {
  const stringKey = getStringKey(key);
  localStorage.removeItem(stringKey);
}
